<template>
  <div class="workspace">
    <aside class="workspace-sidebar">
      <ExecutorHubCard
        v-for="card in cards"
        :key="card.model"
        :title="card.title"
        :filled="!!card.item"
        clickable
        :class="{ active: active === card.model }"
        @click="select(card.model)"
      >
        <template #actions>
          <ExecutorHubActions
            :replaceable="mode === 'detail' || !!card.item"
            @replace="mode === 'wizard' ? selectItem(card.model, null) : $emit('replace', card.model)"
          />
          <svg class="card-arrow" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
            <path d="M2 8h11M9 4l4 4-4 4" />
          </svg>
        </template>
        <ExecutorHubPreview v-if="card.item" :model="card.model" :item="card.item" show-id />
        <EntitySmartSearch
          v-if="mode === 'wizard' && !card.item"
          :model="card.model"
          :itemSelect="null"
          :companyId="card.model === 'managers' ? companyId : null"
          :show-actions="false"
          @update:itemSelect="selectItem(card.model, $event)"
        />
      </ExecutorHubCard>
      <ExecutorHubCard v-if="$slots.summary" :title="summaryTitle">
        <slot name="summary" />
      </ExecutorHubCard>
    </aside>
    <section ref="formRoot" class="workspace-form">
      <component
        :is="formComponent"
        v-if="formComponent && (activeItem || mode === 'wizard')"
        ref="formRef"
        :mode="activeItem ? 'edit' : 'create'"
        :initial-data="activeItem || {}"
        :companyId="active === 'managers' ? companyId : null"
        :company="company"
        :no-trailer="active === 'cars'"
        single-column
        @sendForm="submit"
        @update="submit"
      />
      <template v-if="mode === 'detail' && activeItem">
        <FormCard v-if="active === 'companies'" title="Безопасность компании">
          <CompanySecurityInfo :security="activeSecurity" :companyId="activeItem.ati_id" />
          <div class="security-actions">
            <guard-button
              v-if="accessShow('company')"
              model="companies"
              :id="activeItem.id"
              @reloadExecutor="reloadHandler"
            />
            <span class="security-hint">Повторная проверка ИНН/АТИ</span>
          </div>
        </FormCard>
        <FormCard v-else-if="active === 'managers'" title="Безопасность менеджера">
          <ManagerSecurityInfo :security="activeSecurity" />
          <div class="security-actions">
            <guard-button
              v-if="accessShow('manager')"
              model="managers"
              :id="activeItem.id"
              @reloadExecutor="reloadHandler"
            />
            <span class="security-hint">Повторная проверка АТИ</span>
          </div>
        </FormCard>
        <FormCard v-else-if="active === 'drivers'" title="Безопасность водителя">
          <DriverSecurityInfo :security="activeSecurity" />
          <div class="security-actions">
            <guard-button
              v-if="accessShow('driver')"
              model="drivers"
              :id="activeItem.id"
              @reloadExecutor="reloadHandler"
            />
            <span class="security-hint">Повторная проверка ИНН/паспорта</span>
          </div>
        </FormCard>
        <FormCard v-else-if="active === 'cars'" title="Безопасность авто">
          <CarSecurityInfo :security="activeSecurity" :car="{ vin: activeItem.vin, number: activeItem.number }" />
          <div class="security-actions">
            <guard-button
              v-if="accessShow('car')"
              model="cars"
              :id="activeItem.id"
              @reloadExecutor="reloadHandler"
            />
            <span class="security-hint">Повторная проверка avtocod</span>
          </div>
        </FormCard>
        <FormCard v-else-if="active === 'trailers'" title="Безопасность прицепа">
          <CarSecurityInfo :security="activeSecurity" :car="{ vin: activeItem.vin, number: activeItem.number }" />
          <div class="security-actions">
            <guard-button
              v-if="accessShow('trailer')"
              model="cars"
              :id="activeItem.id"
              @reloadExecutor="reloadHandler"
            />
            <span class="security-hint">Повторная проверка avtocod</span>
          </div>
        </FormCard>
      </template>
      <div v-if="!formComponent || (!activeItem && mode !== 'wizard')" class="workspace-empty">
        {{ mode === 'wizard' ? 'Выберите запись или создайте её' : 'Для этой сущности запись не назначена' }}
      </div>
    </section>
    <div v-if="pending" class="switch-modal" role="dialog" aria-modal="true">
      <div class="switch-modal__panel">
        <p>Текущая форма изменена. Перейти к другой сущности?</p>
        <div class="switch-modal__actions">
          <button type="button" @click="pending = null">Остаться</button>
          <button type="button" @click="switchTo(pending)">Продолжить</button>
          <button type="button" class="primary" @click="saveAndSwitch">Сохранить и продолжить</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import EntitySmartSearch from './EntitySmartSearch.vue'
import ExecutorHubCard from './ExecutorHubCard.vue'
import ExecutorHubPreview from './ExecutorHubPreview.vue'
import ExecutorHubActions from './ExecutorHubActions.vue'
import FormCard from '@/components/formKit/FormCard.vue'
import CompanyForm from '@/components/forms/CompanyForm.vue'
import ManagerForm from '@/components/forms/ManagerForm.vue'
import DriverForm from '@/components/forms/DriverForm.vue'
import CarForm from '@/components/forms/CarForm.vue'
import TrailerForm from '@/components/forms/TrailerForm.vue'
import CompanySecurityInfo from '@/pages/securityCheck/components/CompanySecurityInfo.vue'
import ManagerSecurityInfo from '@/pages/securityCheck/components/ManagerSecurityInfo.vue'
import DriverSecurityInfo from '@/pages/securityCheck/components/DriverSecurityInfo.vue'
import CarSecurityInfo from '@/pages/securityCheck/components/CarSecurityInfo.vue'

const formComponents = { companies: CompanyForm, managers: ManagerForm, drivers: DriverForm, cars: CarForm, trailers: TrailerForm }

export default {
  name: 'ExecutorSplitWorkspace',
  components: {
    EntitySmartSearch,
    ExecutorHubCard,
    ExecutorHubPreview,
    ExecutorHubActions,
    FormCard,
    CompanySecurityInfo,
    ManagerSecurityInfo,
    DriverSecurityInfo,
    CarSecurityInfo,
  },
  props: {
    mode: { type: String, required: true },
    items: { type: Object, required: true },
    executor: { type: Object, default: null },
    activeModel: { type: String, default: 'companies' },
    summaryTitle: { type: String, default: 'Итог' },
    saveHandler: { type: Function, required: true },
    accessShow: { type: Function, default: () => false },
    reloadHandler: { type: Function, default: () => {} },
  },
  emits: ['update:activeModel', 'select', 'replace'],
  setup(props, { emit }) {
    const active = ref(props.activeModel)
    const formRoot = ref(null)
    const formRef = ref(null)
    const dirty = ref(false)
    const pending = ref(null)
    const cards = computed(() => {
      const source = props.mode === 'wizard' ? props.items : {
        companies: props.executor?.company,
        managers: props.executor?.manager,
        drivers: props.executor?.driver,
        cars: props.executor?.car,
        trailers: props.executor?.trailer,
      }
      return [
        ['companies', 'Компания', 'Компания не назначена'], ['managers', 'Менеджер', 'Менеджер не назначен'],
        ['drivers', 'Водитель', 'Водитель не назначен'], ['cars', 'Автомобиль', 'Автомобиль не назначен'],
        ['trailers', 'Прицеп', 'Прицеп не назначен'],
      ].map(([model, title, emptyText]) => ({ model, title, emptyText, item: source[model] }))
    })
    const activeItem = computed(() => cards.value.find(card => card.model === active.value)?.item)
    const activeSecurity = computed(() => {
      const securityInfo = activeItem.value?.security_info
      return Array.isArray(securityInfo) ? securityInfo[0] || null : null
    })
    const formComponent = computed(() => formComponents[active.value])
    const company = computed(() => cards.value.find(card => card.model === 'companies')?.item || null)
    const companyId = computed(() => company.value?.id || null)

    function select(model) {
      if (model === active.value) return
      if (!dirty.value) return switchTo(model)
      pending.value = model
    }
    function switchTo(model) {
      active.value = model
      dirty.value = false
      pending.value = null
      emit('update:activeModel', model)
    }
    function selectItem(model, item) {
      emit('select', model, item)
      select(model)
    }
    async function submit(payload) {
      try {
        const ok = await props.saveHandler(active.value, payload)
        if (ok !== false) {
          dirty.value = false
          if (pending.value) switchTo(pending.value)
        }
      } catch (_error) {
        return
      }
    }
    async function saveAndSwitch() {
      if (!formRef.value || !pending.value) return
      try {
        const result = formRef.value.sendForm ? formRef.value.sendForm() : null
        if (result && typeof result.then === 'function') await result
      } catch (_error) {
        return
      }
      // submit() switches after the form reports a successful save.
    }
    function onInput(event) {
      if (formRoot.value?.contains(event.target)) dirty.value = true
    }
    onMounted(() => window.addEventListener('input', onInput))
    onBeforeUnmount(() => window.removeEventListener('input', onInput))
    watch(() => props.activeModel, value => { active.value = value })
    return { cards, active, activeItem, activeSecurity, formComponent, company, companyId, formRoot, formRef, pending, select, selectItem, submit, saveAndSwitch, switchTo }
  },
}
</script>

<style scoped>
.workspace { display: grid; grid-template-columns: minmax(280px, 330px) minmax(0, 1fr); gap: 18px; align-items: start; }
.workspace-sidebar { display: flex; flex-direction: column; gap: 12px; }
.workspace-sidebar :deep(.hub-card) { padding: 12px; }
.workspace-sidebar :deep(.hub-card) { margin-bottom: 0; }
.workspace-sidebar :deep(.hub-card:focus-within) { z-index: 10; }
.workspace-sidebar :deep(.hub-actions) { justify-content: flex-end; margin-top: 0; }
.card-arrow { color: #94a3b8; height: 16px; margin-left: 2px; width: 16px; }
.card-arrow path { fill: none; stroke: currentColor; stroke-linecap: round; stroke-linejoin: round; stroke-width: 1.25; }
.workspace-sidebar :deep(.form-card-title) { font-size: 14px; }
.workspace-sidebar :deep(.hub-preview-row) { font-size: 12px; }
.workspace-sidebar :deep(.active) { border-color: #4f46e5; box-shadow: 0 0 0 2px rgba(79,70,229,.12); }
.workspace-form { min-width: 0; }
.workspace-empty { background: #fff; border: 1px solid #e5e7eb; border-radius: 10px; color: #6b7280; padding: 28px; text-align: center; }
.workspace-form :deep(.form-card) { margin-top: 0; }
.security-actions { align-items: center; display: flex; flex-wrap: wrap; gap: 10px; margin-top: 12px; }
.security-hint { color: #6b7280; font-size: 12px; }
.switch-modal { position: fixed; inset: 0; z-index: 1200; display: flex; align-items: center; justify-content: center; background: rgba(17,24,39,.45); padding: 16px; }
.switch-modal__panel { width: min(100%, 500px); border-radius: 10px; background: #fff; padding: 22px; box-shadow: 0 18px 40px rgba(15,23,42,.18); }
.switch-modal__actions { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 8px; }
.switch-modal button { border: 1px solid #d1d5db; border-radius: 6px; background: #fff; color: #374151; cursor: pointer; padding: 8px 12px; }
.switch-modal .primary { border-color: #2563eb; background: #2563eb; color: #fff; }
@media (min-width: 1400px) { .workspace { grid-template-columns: minmax(330px, 380px) minmax(0, 1fr); } }
@media (max-width: 800px) { .workspace { grid-template-columns: 1fr; } }
</style>
